import React, { useState, useEffect, useCallback, useRef } from 'react';
import './StoryInspire.css';
import { fetchScreenshots } from './supabaseConfig';
import { database, auth } from '../firebaseConfig';
import { ref, onValue, set, push } from 'firebase/database';

const StoryInspire = () => {
    const [screenshots, setScreenshots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        scene_type: '',
        content_type: '',
        quality_score: 0,
        lighting_type: '',
        time_of_day: '',
        dominant_color: '',
        emotion_score: 0,
        composition_score: 0,
        bokeh_strength: '',
        face_visible: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [ideaLists, setIdeaLists] = useState({});
    const [lastUsedList, setLastUsedList] = useState(null);
    const [showListDropdown, setShowListDropdown] = useState(null);
    const [selectedImageForSave, setSelectedImageForSave] = useState(null);
    const [expandedBoard, setExpandedBoard] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [lightboxImage, setLightboxImage] = useState(null);
    const itemsPerPage = 16;
    const observerRef = useRef();

    const sceneTypes = [
        { value: '', label: 'All Scenes' },
        { value: 'landscape', label: 'Landscape' },
        { value: 'urban', label: 'Urban' },
        { value: 'interior', label: 'Interior' },
        { value: 'portrait', label: 'Portrait' },
        { value: 'lifestyle', label: 'Lifestyle' },
        { value: 'nature', label: 'Nature' },
        { value: 'architecture', label: 'Architecture' },
        { value: 'street', label: 'Street' },
        { value: 'studio', label: 'Studio' }
    ];

    const contentTypes = [
        { value: '', label: 'All Content' },
        { value: 'no_people', label: 'No People' },
        { value: 'person_single', label: 'Single Person' },
        { value: 'person_multiple', label: 'Multiple People' },
        { value: 'couple', label: 'Couple' },
        { value: 'family', label: 'Family' },
        { value: 'group', label: 'Group' },
        { value: 'animal', label: 'Animal' },
        { value: 'object', label: 'Object' }
    ];

    const lightingTypes = [
        { value: '', label: 'All Lighting' },
        { value: 'natural', label: 'Natural Light' },
        { value: 'studio', label: 'Studio Light' },
        { value: 'artificial', label: 'Artificial Light' },
        { value: 'mixed', label: 'Mixed Light' },
        { value: 'low_light', label: 'Low Light' },
        { value: 'dramatic', label: 'Dramatic Light' }
    ];

    const timeOfDayOptions = [
        { value: '', label: 'Any Time' },
        { value: 'morning', label: 'Morning' },
        { value: 'noon', label: 'Noon' },
        { value: 'afternoon', label: 'Afternoon' },
        { value: 'evening', label: 'Evening' },
        { value: 'night', label: 'Night' },
        { value: 'golden_hour', label: 'Golden Hour' },
        { value: 'blue_hour', label: 'Blue Hour' }
    ];

    const dominantColors = [
        { value: 'red', label: 'Red', color: '#ef4444' },
        { value: 'blue', label: 'Blue', color: '#3b82f6' },
        { value: 'green', label: 'Green', color: '#10b981' },
        { value: 'yellow', label: 'Yellow', color: '#f59e0b' },
        { value: 'orange', label: 'Orange', color: '#f97316' },
        { value: 'purple', label: 'Purple', color: '#8b5cf6' },
        { value: 'pink', label: 'Pink', color: '#ec4899' },
        { value: 'brown', label: 'Brown', color: '#a16207' },
        { value: 'black', label: 'Black', color: '#1f2937' },
        { value: 'white', label: 'White', color: '#f9fafb' },
        { value: 'gray', label: 'Gray', color: '#6b7280' }
    ];

    const bokehOptions = [
        { value: '', label: 'Any Bokeh' },
        { value: 'none', label: 'No Bokeh' },
        { value: 'soft', label: 'Soft Bokeh' },
        { value: 'strong', label: 'Strong Bokeh' },
        { value: 'cinematic', label: 'Cinematic Bokeh' }
    ];

    const faceVisibleOptions = [
        { value: '', label: 'Any Face' },
        { value: 'true', label: 'Face Visible' },
        { value: 'false', label: 'No Face Visible' }
    ];

    const buildApiFilters = useCallback(() => {
        const apiFilters = {};
        if (filters.scene_type) apiFilters.scene_type = filters.scene_type;
        if (filters.content_type) apiFilters.content_type = filters.content_type;
        if (filters.lighting_type) apiFilters.lighting_type = filters.lighting_type;
        if (filters.time_of_day) apiFilters.time_of_day = filters.time_of_day;
        if (filters.dominant_color) apiFilters.dominant_color = filters.dominant_color;
        if (filters.bokeh_strength) apiFilters.bokeh_strength = filters.bokeh_strength;
        if (filters.face_visible) apiFilters.face_visible = filters.face_visible;
        if (filters.quality_score > 0) apiFilters.quality_score = filters.quality_score;
        if (filters.emotion_score > 0) apiFilters.emotion_score = filters.emotion_score;
        if (filters.composition_score > 0) apiFilters.composition_score = filters.composition_score;
        return apiFilters;
    }, [filters]);

    const loadScreenshots = useCallback(async (reset = false) => {
        if (reset) {
            setLoading(true);
            setCurrentPage(1);
            setHasMore(true);
        } else {
            setLoadingMore(true);
        }

        try {
            const pageToFetch = reset ? 1 : currentPage;
            const offset = (pageToFetch - 1) * itemsPerPage;

            const { data, total } = await fetchScreenshots({
                filters: buildApiFilters(),
                search: searchTerm.trim(),
                orderBy: 'id',
                ascending: false,
                offset,
                limit: itemsPerPage,
            });

            const newData = data || [];

            if (reset) {
                setScreenshots(newData);
                setCurrentPage(2);
            } else {
                setScreenshots(prev => [...prev, ...newData]);
                setCurrentPage(prev => prev + 1);
            }

            setTotalCount(total || 0);
            const totalLoaded = reset ? newData.length : screenshots.length + newData.length;
            setHasMore(newData.length === itemsPerPage && totalLoaded < (total || 0));

        } catch (err) {
            console.error('Error fetching screenshots:', err);
            setHasMore(false);
            if (reset) {
                setScreenshots([]);
                setTotalCount(0);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [buildApiFilters, searchTerm, currentPage, itemsPerPage]);

    // Debounced fetch with search term - reset on filter/search change
    useEffect(() => {
        const timer = setTimeout(() => {
            loadScreenshots(true);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm, filters]);

    // Infinite scroll observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
                    loadScreenshots(false);
                }
            },
            { threshold: 0.1 }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => {
            if (observerRef.current) {
                observer.unobserve(observerRef.current);
            }
        };
    }, [loadScreenshots, hasMore, loading, loadingMore]);

    // Load user's idea lists from Firebase
    useEffect(() => {
        if (!auth.currentUser) return;

        const userId = auth.currentUser.uid;
        const listsRef = ref(database, `stories/${userId}/ideas`);

        const unsubscribe = onValue(listsRef, (snapshot) => {
            if (snapshot.exists()) {
                setIdeaLists(snapshot.val());
            } else {
                setIdeaLists({});
            }
        });

        return () => unsubscribe();
    }, []);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const clearAllFilters = () => {
        setFilters({
            scene_type: '',
            content_type: '',
            quality_score: 0,
            lighting_type: '',
            time_of_day: '',
            dominant_color: '',
            emotion_score: 0,
            composition_score: 0,
            bokeh_strength: '',
            face_visible: ''
        });
        setSearchTerm('');
        setCurrentPage(1);
    };

    const isImageInScene = (imageId, scene) => {
        return (scene?.imageIds || []).includes(imageId);
    };

    const handleSaveToList = (screenshot, event) => {
        event.stopPropagation();
        setSelectedImageForSave(screenshot);
        setExpandedBoard(null);

        if (Object.keys(ideaLists).length === 0) {
            setShowCreateModal(true);
            return;
        }

        setShowListDropdown(showListDropdown === screenshot.id ? null : screenshot.id);
    };

    const saveToScene = async (imageId, boardId, sceneId) => {
        if (!auth.currentUser || !ideaLists[boardId]) return;

        const userId = auth.currentUser.uid;
        const scene = ideaLists[boardId]?.scenes?.[sceneId];
        if (!scene) return;

        const currentImageIds = scene.imageIds || [];
        if (currentImageIds.includes(imageId)) {
            showTemporaryNotification('Image already in this scene', 'error');
            return;
        }

        const sceneRef = ref(database, `stories/${userId}/ideas/${boardId}/scenes/${sceneId}`);
        try {
            await set(sceneRef, {
                ...scene,
                imageIds: [...currentImageIds, imageId]
            });
            setLastUsedList(boardId);
            setShowListDropdown(null);
            setSelectedImageForSave(null);
            setExpandedBoard(null);
            const boardName = ideaLists[boardId].name;
            const sceneName = scene.name;
            showTemporaryNotification(`Saved to "${boardName} > ${sceneName}"`);
        } catch (error) {
            console.error('Error saving image:', error);
            showTemporaryNotification('Error saving image', 'error');
        }
    };

    const createNewBoardAndSave = async (boardName, screenshot = null) => {
        const imageToSave = screenshot || selectedImageForSave;
        if (!boardName.trim() || !auth.currentUser || !imageToSave) return;

        const userId = auth.currentUser.uid;
        const boardRef = push(ref(database, `stories/${userId}/ideas`));
        const firstSceneRef = push(ref(database, `stories/${userId}/ideas/${boardRef.key}/scenes`));

        try {
            await set(boardRef, {
                name: boardName.trim(),
                createdAt: Date.now(),
                scenes: {
                    [firstSceneRef.key]: {
                        name: 'Scene 1',
                        description: '',
                        createdAt: Date.now(),
                        imageIds: [imageToSave.id],
                        uploadedImages: []
                    }
                }
            });
            setLastUsedList(boardRef.key);
            setShowListDropdown(null);
            setSelectedImageForSave(null);
            setExpandedBoard(null);
            showTemporaryNotification(`Created "${boardName}" and saved image!`);
        } catch (error) {
            console.error('Error creating board:', error);
            showTemporaryNotification('Error creating board', 'error');
        }
    };

    // Temporary notification system
    const [notification, setNotification] = useState(null);
    
    const showTemporaryNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setShowListDropdown(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const navigateLightbox = useCallback((direction) => {
        if (!lightboxImage) return;
        const currentIndex = screenshots.findIndex(s => s.id === lightboxImage.id);
        if (currentIndex === -1) return;
        const nextIndex = currentIndex + direction;
        if (nextIndex >= 0 && nextIndex < screenshots.length) {
            setLightboxImage(screenshots[nextIndex]);
        }
    }, [lightboxImage, screenshots]);

    useEffect(() => {
        if (!lightboxImage) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setLightboxImage(null);
            if (e.key === 'ArrowLeft') navigateLightbox(-1);
            if (e.key === 'ArrowRight') navigateLightbox(1);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [lightboxImage, navigateLightbox]);

    const renderLoadingMore = () => {
        if (!loadingMore || !hasMore) return null;
        
        return (
            <div className="story-inspire-loading-more">
                <div className="story-inspire-spinner"></div>
                <p>Loading more images...</p>
            </div>
        );
    };

    const renderScrollTrigger = () => {
        if (!hasMore || loading) return null;
        
        return (
            <div 
                ref={observerRef} 
                className="story-inspire-scroll-trigger"
                style={{ height: '20px', margin: '20px 0' }}
            />
        );
    };

    return (
        <div className="story-inspire-container">
            <div className="story-inspire-filters">
                <div className="story-inspire-search-bar">
                    <input
                        type="text"
                        placeholder="Search for inspiration..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="story-inspire-search-input"
                    />
                    <svg className="story-inspire-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="M21 21l-4.35-4.35"></path>
                    </svg>
                </div>
                
                {/* Color Palette Filter */}
                <div className="story-inspire-filter-group">
                    <h4 className="story-inspire-filter-title">Colors</h4>
                    <div className="story-inspire-color-filters">
                        <button
                            className={`story-inspire-color-chip ${filters.dominant_color === '' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('dominant_color', '')}
                            title="All Colors"
                        >
                            <div className="story-inspire-color-all">
                                <div className="color-strip" style={{background: 'linear-gradient(45deg, #ef4444, #3b82f6, #10b981, #f59e0b)'}}></div>
                            </div>
                        </button>
                        {dominantColors.map(color => (
                            <button
                                key={color.value}
                                className={`story-inspire-color-chip ${filters.dominant_color === color.value ? 'active' : ''}`}
                                onClick={() => handleFilterChange('dominant_color', color.value)}
                                title={color.label}
                            >
                                <div 
                                    className="story-inspire-color-swatch"
                                    style={{
                                        backgroundColor: color.color,
                                        border: color.value === 'white' ? '1px solid #e5e7eb' : 'none'
                                    }}
                                ></div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Scene & Content Filter */}
                <div className="story-inspire-filter-group">
                    <h4 className="story-inspire-filter-title">Scene & Content</h4>
                    <div className="story-inspire-tag-filters">
                        {sceneTypes.filter(type => type.value !== '').map(type => (
                            <button
                                key={type.value}
                                className={`story-inspire-filter-tag ${filters.scene_type === type.value ? 'active' : ''}`}
                                onClick={() => handleFilterChange('scene_type', filters.scene_type === type.value ? '' : type.value)}
                            >
                                {type.label}
                            </button>
                        ))}

                        <div className="story-inspire-filter-divider"></div>
                        {contentTypes.filter(type => type.value !== '').map(type => (
                            <button
                                key={type.value}
                                className={`story-inspire-filter-tag ${filters.content_type === type.value ? 'active' : ''}`}
                                onClick={() => handleFilterChange('content_type', filters.content_type === type.value ? '' : type.value)}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Lighting & Time Filter */}
                <div className="story-inspire-filter-group">
                    <h4 className="story-inspire-filter-title">Lighting & Time</h4>
                    <div className="story-inspire-tag-filters">
                        {lightingTypes.filter(type => type.value !== '').map(type => (
                            <button
                                key={type.value}
                                className={`story-inspire-filter-tag ${filters.lighting_type === type.value ? 'active' : ''}`}
                                onClick={() => handleFilterChange('lighting_type', filters.lighting_type === type.value ? '' : type.value)}
                            >
                                {type.label}
                            </button>
                        ))}
                        <div className="story-inspire-filter-divider"></div>
                        {timeOfDayOptions.filter(time => time.value !== '').map(time => (
                            <button
                                key={time.value}
                                className={`story-inspire-filter-tag ${filters.time_of_day === time.value ? 'active' : ''}`}
                                onClick={() => handleFilterChange('time_of_day', filters.time_of_day === time.value ? '' : time.value)}
                            >
                                {time.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Effects & Quality Filter */}
                <div className="story-inspire-filter-group">
                    <h4 className="story-inspire-filter-title">Effects & Quality</h4>
                    <div className="story-inspire-tag-filters">
                        {bokehOptions.filter(bokeh => bokeh.value !== '').map(bokeh => (
                            <button
                                key={bokeh.value}
                                className={`story-inspire-filter-tag ${filters.bokeh_strength === bokeh.value ? 'active' : ''}`}
                                onClick={() => handleFilterChange('bokeh_strength', filters.bokeh_strength === bokeh.value ? '' : bokeh.value)}
                            >
                                {bokeh.label}
                            </button>
                        ))}
                        {faceVisibleOptions.filter(face => face.value !== '').map(face => (
                            <button
                                key={face.value}
                                className={`story-inspire-filter-tag ${filters.face_visible === face.value ? 'active' : ''}`}
                                onClick={() => handleFilterChange('face_visible', filters.face_visible === face.value ? '' : face.value)}
                            >
                                {face.label}
                            </button>
                        ))}
                        <div className="story-inspire-filter-divider"></div>
                        <button
                            className={`story-inspire-filter-tag quality ${filters.quality_score === 0 ? 'active' : ''}`}
                            onClick={() => handleFilterChange('quality_score', 0)}
                        >
                            All Quality
                        </button>
                        <button
                            className={`story-inspire-filter-tag quality ${filters.quality_score === 9 ? 'active' : ''}`}
                            onClick={() => handleFilterChange('quality_score', 9)}
                        >
                            ⭐ Exceptional (9+)
                        </button>
                        <button
                            className={`story-inspire-filter-tag quality ${filters.quality_score === 8 ? 'active' : ''}`}
                            onClick={() => handleFilterChange('quality_score', 8)}
                        >
                            ⭐ High (8+)
                        </button>
                        <button
                            className={`story-inspire-filter-tag quality ${filters.quality_score === 7 ? 'active' : ''}`}
                            onClick={() => handleFilterChange('quality_score', 7)}
                        >
                            ⭐ Good (7+)
                        </button>
                        <button
                            className={`story-inspire-filter-tag emotion ${filters.emotion_score === 0 ? 'active' : ''}`}
                            onClick={() => handleFilterChange('emotion_score', 0)}
                        >
                            All Emotion
                        </button>
                        <button
                            className={`story-inspire-filter-tag emotion ${filters.emotion_score === 8 ? 'active' : ''}`}
                            onClick={() => handleFilterChange('emotion_score', 8)}
                        >
                            ❤️ High Emotion (8+)
                        </button>
                        <button
                            className={`story-inspire-filter-tag composition ${filters.composition_score === 0 ? 'active' : ''}`}
                            onClick={() => handleFilterChange('composition_score', 0)}
                        >
                            All Composition
                        </button>
                        <button
                            className={`story-inspire-filter-tag composition ${filters.composition_score === 8 ? 'active' : ''}`}
                            onClick={() => handleFilterChange('composition_score', 8)}
                        >
                            🎨 Great Composition (8+)
                        </button>
                    </div>
                </div>
            </div>

            <div className="story-inspire-stats">
                <span className="story-inspire-count">
                    {totalCount.toLocaleString()} images found
                </span>
                <button 
                    onClick={clearAllFilters}
                    className="story-inspire-clear-btn"
                >
                    Clear All Filters
                </button>
            </div>

            {loading ? (
                <div className="story-inspire-loading">
                    <div className="story-inspire-spinner"></div>
                    <p>Loading inspiration...</p>
                </div>
            ) : (
                <>
                    <div className="story-inspire-grid">
                        {screenshots.map((screenshot) => (
                            <div key={screenshot.id} className="story-inspire-card">
                                <div className="story-inspire-image-container" onClick={() => setLightboxImage(screenshot)}>
                                    <img
                                        src={screenshot.supabase_storage_url}
                                        alt={screenshot.description || screenshot.filename}
                                        className="story-inspire-image"
                                        loading="lazy"
                                    />
                                    <div className="story-inspire-overlay">
                                                                                <div className="story-inspire-top-section">
                                            <div className="story-inspire-scores">
                                                <span className="story-inspire-score quality">
                                                    ⭐ {screenshot.quality_score?.toFixed(1)}
                                                </span>
                                                {screenshot.emotion_score && (
                                                    <span className="story-inspire-score emotion">
                                                        ❤️ {screenshot.emotion_score.toFixed(1)}
                                                    </span>
                                                )}
                                                {screenshot.composition_score && (
                                                    <span className="story-inspire-score composition">
                                                        🎨 {screenshot.composition_score.toFixed(1)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="story-inspire-bottom-section">
                                            <div className="story-inspire-add-container">
                                                <button 
                                                    className="story-inspire-add-btn"
                                                    onClick={(e) => handleSaveToList(screenshot, e)}
                                                    title="Add to Ideas list"
                                                >
                                                    <span className="story-inspire-plus-icon">+</span>
                                                </button>
                                                
                                                {/* Board/Scene Dropdown */}
                                                {showListDropdown === screenshot.id && (
                                                    <div className="story-inspire-add-dropdown" onClick={(e) => e.stopPropagation()}>
                                                        <div className="story-inspire-dropdown-header">
                                                            {expandedBoard ? (
                                                                <button
                                                                    className="story-inspire-dropdown-back"
                                                                    onClick={() => setExpandedBoard(null)}
                                                                >
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="15 18 9 12 15 6"></polyline>
                                                                    </svg>
                                                                    {ideaLists[expandedBoard]?.name}
                                                                </button>
                                                            ) : (
                                                                <span>Add to board:</span>
                                                            )}
                                                        </div>

                                                        <div className="story-inspire-dropdown-content">
                                                            {!expandedBoard ? (
                                                                Object.keys(ideaLists)
                                                                    .sort((a, b) => (ideaLists[b].createdAt || 0) - (ideaLists[a].createdAt || 0))
                                                                    .map(boardId => {
                                                                        const board = ideaLists[boardId];
                                                                        const sceneCount = Object.keys(board.scenes || {}).length;
                                                                        return (
                                                                            <button
                                                                                key={boardId}
                                                                                className="story-inspire-dropdown-item"
                                                                                onClick={() => setExpandedBoard(boardId)}
                                                                            >
                                                                                <span className="dropdown-list-name">
                                                                                    {board.name}
                                                                                </span>
                                                                                <span className="dropdown-list-count">
                                                                                    {sceneCount} {sceneCount === 1 ? 'scene' : 'scenes'}
                                                                                </span>
                                                                                <svg className="dropdown-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                                    <polyline points="9 18 15 12 9 6"></polyline>
                                                                                </svg>
                                                                            </button>
                                                                        );
                                                                    })
                                                            ) : (
                                                                Object.keys(ideaLists[expandedBoard]?.scenes || {})
                                                                    .sort((a, b) => {
                                                                        const scA = ideaLists[expandedBoard].scenes[a];
                                                                        const scB = ideaLists[expandedBoard].scenes[b];
                                                                        return (scA.createdAt || 0) - (scB.createdAt || 0);
                                                                    })
                                                                    .map(sceneId => {
                                                                        const scene = ideaLists[expandedBoard].scenes[sceneId];
                                                                        const alreadyIn = isImageInScene(screenshot.id, scene);
                                                                        const imgCount = (scene.imageIds || []).length + (scene.uploadedImages || []).length;
                                                                        return (
                                                                            <button
                                                                                key={sceneId}
                                                                                className={`story-inspire-dropdown-item ${alreadyIn ? 'already-added' : ''}`}
                                                                                onClick={() => {
                                                                                    if (!alreadyIn) saveToScene(screenshot.id, expandedBoard, sceneId);
                                                                                }}
                                                                                disabled={alreadyIn}
                                                                            >
                                                                                <span className="dropdown-list-name">
                                                                                    {scene.name}
                                                                                    {alreadyIn && <span className="already-added-text"> (already added)</span>}
                                                                                </span>
                                                                                <span className="dropdown-list-count">
                                                                                    {imgCount}
                                                                                </span>
                                                                                {alreadyIn && (
                                                                                    <span className="dropdown-already-added">✓</span>
                                                                                )}
                                                                            </button>
                                                                        );
                                                                    })
                                                            )}
                                                        </div>

                                                        <div className="story-inspire-dropdown-footer">
                                                            <div className="story-inspire-dropdown-divider"></div>
                                                            <button
                                                                className="story-inspire-dropdown-create"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedImageForSave(screenshot);
                                                                    setShowListDropdown(null);
                                                                    setExpandedBoard(null);
                                                                    setShowCreateModal(true);
                                                                }}
                                                            >
                                                                + Create new board
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="story-inspire-meta">
                                    <div className="story-inspire-tags">
                                        <span className="story-inspire-tag scene">
                                            {screenshot.scene_type}
                                        </span>
                                        <span className="story-inspire-tag content">
                                            {screenshot.content_type?.replace('_', ' ')}
                                        </span>
                                        {screenshot.lighting_type && (
                                            <span className="story-inspire-tag lighting">
                                                💡 {screenshot.lighting_type}
                                            </span>
                                        )}
                                        {screenshot.time_of_day && (
                                            <span className="story-inspire-tag time">
                                                🕐 {screenshot.time_of_day.replace('_', ' ')}
                                            </span>
                                        )}
                                        {screenshot.dominant_color && (
                                            <span 
                                                className="story-inspire-color-dot"
                                                style={{ backgroundColor: screenshot.dominant_color }}
                                                title={screenshot.dominant_color}
                                            ></span>
                                        )}
                                    </div>
                                    
                                    {screenshot.description && (
                                        <p className="story-inspire-description">
                                            {screenshot.description.substring(0, 100)}...
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {renderScrollTrigger()}
                    {renderLoadingMore()}
                    
                    {!hasMore && screenshots.length > 0 && (
                        <div className="story-inspire-end-message">
                            <p>🎉 You've seen all {totalCount.toLocaleString()} images!</p>
                        </div>
                    )}
                </>
            )}

            {/* Lightbox Modal */}
            {lightboxImage && (() => {
                const currentIndex = screenshots.findIndex(s => s.id === lightboxImage.id);
                const hasPrev = currentIndex > 0;
                const hasNext = currentIndex < screenshots.length - 1;

                return (
                    <div className="story-inspire-lightbox-overlay" onClick={() => setLightboxImage(null)}>
                        <button
                            className="story-inspire-lightbox-close"
                            onClick={() => setLightboxImage(null)}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>

                        {hasPrev && (
                            <button
                                className="story-inspire-lightbox-nav story-inspire-lightbox-prev"
                                onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
                            >
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6"></polyline>
                                </svg>
                            </button>
                        )}

                        <img
                            src={lightboxImage.supabase_storage_url}
                            alt={lightboxImage.description || lightboxImage.filename}
                            className="story-inspire-lightbox-image"
                            onClick={(e) => e.stopPropagation()}
                        />

                        {hasNext && (
                            <button
                                className="story-inspire-lightbox-nav story-inspire-lightbox-next"
                                onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
                            >
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </button>
                        )}

                        <div className="story-inspire-lightbox-counter" onClick={(e) => e.stopPropagation()}>
                            {currentIndex + 1} / {screenshots.length}
                        </div>
                    </div>
                );
            })()}

            {/* Create Board Modal */}
            {showCreateModal && (
                <div className="story-inspire-create-modal-overlay">
                    <div className="story-inspire-create-modal">
                        <div className="story-inspire-create-modal-header">
                            <h3>Create New Board</h3>
                            <button
                                className="story-inspire-create-modal-close"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewListName('');
                                    setSelectedImageForSave(null);
                                }}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className="story-inspire-create-modal-content">
                            <label className="story-inspire-create-modal-label">
                                Board Name
                            </label>
                            <input
                                type="text"
                                className="story-inspire-create-modal-input"
                                placeholder="Enter a name for your new board..."
                                value={newListName}
                                onChange={(e) => setNewListName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newListName.trim()) {
                                        createNewBoardAndSave(newListName.trim(), selectedImageForSave);
                                        setShowCreateModal(false);
                                        setNewListName('');
                                    }
                                }}
                                autoFocus
                            />
                        </div>
                        
                        <div className="story-inspire-create-modal-actions">
                            <button
                                className="story-inspire-create-modal-cancel"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setNewListName('');
                                    setSelectedImageForSave(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="story-inspire-create-modal-submit"
                                onClick={() => {
                                    if (newListName.trim()) {
                                        createNewBoardAndSave(newListName.trim(), selectedImageForSave);
                                        setShowCreateModal(false);
                                        setNewListName('');
                                    }
                                }}
                                disabled={!newListName.trim()}
                            >
                                Create Board
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification System */}
            {notification && (
                <div className={`story-inspire-notification ${notification.type}`}>
                    {notification.message}
                </div>
            )}
        </div>
    );
};

export default StoryInspire; 